import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import env from '@travel/env';
import { useHasMounted, useTranslation } from '@travel/traveler-core/hooks';
import useMemberKeepAlive from '@travel/traveler-core/hooks/useMemberKeepAlive';
import { lazyWithSuspense } from '@travel/utils';

import Snackbar from 'components/Snackbar';
import ErrorPage from 'pages/ErrorPage';

import { pushLocation } from 'store/__router/actions';
import { refreshAuthCode } from 'store/authCode/actions';
import { getIsLoggedIn } from 'store/authCode/selectors';
import { getItem as getChat } from 'store/chat/selectors';
import { getIsAPIError } from 'store/commonError/selectors';
import { NotificationLines } from 'store/notification/enum';
import { getItem } from 'store/notification/selectors';

import { SITE_IMAGE_URL } from 'constants/common';
import useHeaderEvents from 'hooks/useHeaderEvents';

import SEO from './components/SEO';

import styles from '../style/app.module.scss';

// Load client-only components to avoid SSR hydration mismatches
const ChatBox = lazyWithSuspense(() => import('components/ChatBox'), { ssr: false });
const Pitari = lazyWithSuspense(() => import('components/Pitari'), { ssr: false });

const App = (props: any) => {
  const dispatch = useDispatch();
  const hasMounted = useHasMounted();

  const isAPIError = useSelector(getIsAPIError);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const notification = useSelector(getItem);
  const chat = useSelector(getChat);

  const chatStyle = notification.isOpen
    ? notification.lines === NotificationLines.SINGLE
      ? styles.chatBoxSingle
      : styles.chatBoxMulti
    : '';

  const normalPageTitle = useTranslation({ id: 'Booking_List.Header.HTML_Title' });
  // refresh token
  const refreshTokenHookCallback = useCallback(() => {
    dispatch(refreshAuthCode());
  }, [dispatch]);

  useHeaderEvents();
  useMemberKeepAlive(isLoggedIn, refreshTokenHookCallback);

  const envName = env('UNIVERSAL_ENV_NAME');

  let content = props.children;
  if (isAPIError) content = <ErrorPage />;

  return (
    <div id="app" data-testid="app-wrapper" className={styles.appWrapper}>
      <SEO title={normalPageTitle} imageUrl={SITE_IMAGE_URL} />
      {hasMounted && envName && (
        <button
          data-testid="app-env-button"
          className={styles.envButton}
          tabIndex={-1}
          onClick={() => {
            dispatch(
              pushLocation(
                window.location.pathname.replace(env('UNIVERSAL_BASE_NAME') as string, '') +
                  window.location.search,
              ),
            );
          }}
        >
          {envName}
        </button>
      )}
      <div>{content}</div>
      {isLoggedIn && <Pitari />}
      <div dangerouslySetInnerHTML={{ __html: '<global-footer />' }} />

      {chat.isExists && <ChatBox url={chat.url} text={chat.text} className={chatStyle} />}

      <Snackbar
        callback={notification?.callback}
        isOpen={notification.isOpen}
        icon={notification?.icon}
      >
        {notification.context}
      </Snackbar>
    </div>
  );
};

export default App;
